import { css } from '@emotion/react';

import { SECONDARY_FONT_FAMILY_HOS } from 'src/styles/constants';
import { getTheme } from 'src/utils/getTheme';

const { theme, isHosTheme } = getTheme();

const CONTENT_MAX_WIDTH = '1200px';

export const styles = {
    container: css({
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        marginBottom: '80px',
    }),

    header: css({
        width: '100%',
        padding: '40px 20px',
        background: isHosTheme ? theme.palette.background.paper : theme.palette.common.black,
        [theme.breakpoints.up('lg')]: {
            padding: '60px 20px',
        },
    }),

    headerContent: css({
        maxWidth: CONTENT_MAX_WIDTH,
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
    }),

    backButton: css({
        alignSelf: 'flex-start',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        marginBottom: '20px',
        padding: 0,
        border: 'none',
        background: 'none',
        cursor: 'pointer',
        color: isHosTheme ? theme.palette.common.white : theme.palette.customBlue,
        textDecoration: isHosTheme ? 'underline' : 'none',
        ':hover': {
            textDecoration: 'underline',
        },
    }),

    backIcon: css({
        '.arrow-color': {
            fill: isHosTheme ? theme.palette.common.white : theme.palette.customBlue,
        },
    }),

    // Matches the red stripe above <Section /> headers, e.g. on <PaymentPlan />
    titleBlock: css({
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        ...(!isHosTheme && {
            marginTop: '18px',
            '::before': {
                content: '""',
                position: 'absolute',
                top: '-18px',
                left: 0,
                width: '70px',
                height: '6px',
                background: theme.palette.primary.main,
            },
        }),
    }),

    subtitle: css({
        color: isHosTheme ? theme.palette.primary.main : theme.palette.grey[400],
        textTransform: 'uppercase',
    }),

    title: css({
        color: theme.palette.common.white,
        ...(isHosTheme && {
            fontFamily: SECONDARY_FONT_FAMILY_HOS,
        }),
    }),

    infoRow: css({
        margin: '20px 0 0 0',
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: '20px',
        [theme.breakpoints.up('lg')]: {
            display: 'flex',
            flexWrap: 'wrap',
            gap: '50px',
        },
    }),

    infoItem: css({
        display: 'flex',
        flexDirection: 'column',
        gap: '5px',
    }),

    infoLabel: css({
        color: isHosTheme ? theme.palette.common.white : theme.palette.grey[400],
    }),

    infoValue: css({
        margin: 0,
        color: isHosTheme ? theme.palette.primary.main : theme.palette.common.white,
    }),

    // Default skeleton colour is dark, so lighten it to show up on the dark header
    infoValueSkeleton: css({
        width: '80px',
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
    }),

    titleSkeleton: css({
        width: '300px',
        maxWidth: '100%',
        fontSize: theme.typography.h1.fontSize,
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
    }),

    subtitleSkeleton: css({
        width: '140px',
        fontSize: theme.typography.h4.fontSize,
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
    }),

    infoLabelSkeleton: css({
        width: '60px',
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
    }),

    content: css({
        width: '100%',
        maxWidth: CONTENT_MAX_WIDTH,
        margin: '0 auto',
        padding: '40px 20px 0 20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '40px',
        [theme.breakpoints.up('lg')]: {
            flexDirection: 'row',
            alignItems: 'flex-start',
        },
    }),

    carouselColumn: css({
        width: '100%',
        minWidth: 0,
        [theme.breakpoints.up('lg')]: {
            flex: '3 1 0',
        },
    }),

    imageDisclaimer: css({
        marginTop: '10px',
        color: theme.palette.text.secondary,
    }),

    breakdownColumn: css({
        width: '100%',
        [theme.breakpoints.up('lg')]: {
            flex: '2 1 0',
            position: 'sticky',
            top: '20px',
        },
    }),

    carouselSkeleton: css({
        width: '100%',
        height: 'auto',
        aspectRatio: '4 / 3',
        ...(isHosTheme && {
            borderRadius: theme.custom.borderRadius[23],
        }),
    }),

    carousel: css({
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
    }),

    carouselMain: css({
        position: 'relative',
        width: '100%',
        aspectRatio: '4 / 3',
        overflow: 'hidden',
        background: theme.palette.grey[200],
        ...(isHosTheme && {
            borderRadius: theme.custom.borderRadius[23],
        }),
    }),

    carouselImage: css({
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        display: 'block',
    }),

    carouselControl: css({
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        color: theme.palette.common.black,
        background: theme.palette.common.white,
        '&:hover': {
            background: theme.palette.grey[200],
        },
    }),

    carouselControlPrev: css({
        left: '15px',
    }),

    carouselControlNext: css({
        right: '15px',
    }),

    carouselCounter: css({
        position: 'absolute',
        bottom: '15px',
        right: '15px',
        padding: '4px 10px',
        color: theme.palette.common.white,
        background: 'rgba(0, 0, 0, 0.6)',
        borderRadius: '100px',
    }),

    thumbnails: css({
        display: 'flex',
        gap: '10px',
        overflowX: 'auto',
    }),

    thumbnail: css({
        position: 'relative',
        flex: '0 0 auto',
        width: '100px',
        height: '75px',
        padding: 0,
        border: '2px solid transparent',
        background: 'none',
        cursor: 'pointer',
        opacity: 0.6,
        overflow: 'hidden',
        ...(isHosTheme && {
            borderRadius: '10px',
        }),
    }),

    thumbnailActive: css({
        opacity: 1,
        borderColor: isHosTheme ? theme.palette.primary.main : theme.palette.common.black,
    }),

    thumbnailImage: css({
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        display: 'block',
    }),

    breakdown: css({
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '30px',
    }),

    studentCounter: css({
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '25px 20px',
        border: isHosTheme
            ? `0.5px solid ${theme.palette.secondary.dark}`
            : `1px solid ${theme.palette.grey[600]}`,
        ...(isHosTheme && {
            borderRadius: theme.custom.borderRadius[23],
            backgroundColor: theme.palette.common.white,
        }),
    }),

    studentCounterSkeleton: css({
        width: '100%',
        height: '80px',
        ...(isHosTheme && {
            borderRadius: theme.custom.borderRadius[23],
        }),
    }),

    counter: css({
        display: 'flex',
        alignItems: 'center',
        gap: '15px',
    }),

    counterButton: css({
        display: 'flex',
        padding: '4px',
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: '50%',
        border: `1px solid ${
            isHosTheme ? theme.palette.secondary.dark : theme.palette.text.secondary
        }`,
        background: 'none',
        cursor: 'pointer',
        ':active': {
            background: theme.palette.text.placeholder,
        },
    }),

    counterButtonDisabled: css({
        border: `1px solid ${theme.palette.text.disabled}`,
        cursor: 'not-allowed',
        ':active': {
            background: 'none',
        },
    }),

    instalmentSelector: css({
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '10px',
    }),

    instalmentOptions: css({
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        [theme.breakpoints.up('sm')]: {
            flexDirection: 'row',
        },
    }),

    paymentPlanSkeleton: css({
        width: '100%',
        height: '320px',
        ...(isHosTheme && {
            borderRadius: theme.custom.borderRadius[23],
        }),
    }),

    ctaSkeleton: css({
        width: '100%',
        height: '50px',
        borderRadius: '100px',
    }),

    cta: css({
        width: '100%',
        borderRadius: '100px',
        paddingBlock: '16px',
    }),
};
